import type { EventBus } from "../core/EventBus.ts";
import type { Card } from "../entities/types.ts";
import { BeatScheduler } from "./BeatScheduler.ts";
import { CommsMomentum, type RivalryTone } from "./CommsMomentum.ts";
import { COMMANDER_QUIPS } from "./commanderQuips.ts";
import { commsLine } from "./commsI18n.ts";
import {
  STORY_BEATS,
  type StoryBeat,
  type StoryTrigger,
} from "./storyContent.ts";
import {
  createDomMascotSurface,
  type MascotSurface,
  type TvPopOutBeat,
  type VeraPose,
} from "./OperatorMascot.ts";
import {
  BEAT,
  PRIORITY,
  type BeatPriority,
  type BeatSpec,
  VERA_BARRAGE,
  VERA_BOSS_DOWN,
  VERA_COMEBACK,
  VERA_CORE_HIT,
  VERA_CRITICAL,
  VERA_FIRST_BLOOD,
  VERA_FIRST_LEAK,
  VERA_FLAWLESS,
  VERA_GAME_OVER,
  VERA_HEAVY_HIT,
  VERA_NEW_SIGNATURE,
  VERA_NODE_LOST,
  VERA_RUN_START,
  VERA_SPAWN,
  VERA_STEALTH_FIRST,
  VERA_STEALTH_REPEAT,
  VERA_SWARM,
  VERA_UPGRADE_TIP,
  VERA_WAVE_PRAISE,
  VERA_WAVE_PRAISE_DOMINANT,
  VERA_WAVE_PRAISE_RELIEF,
  VERA_WAVE_PRAISE_FINALE,
  VERA_KERNEL_PANIC,
  VERA_PAYLOAD_LAUNCH,
  VERA_VICTORY,
  VERA_VICTORY_HARDWON,
  VERA_ENDLESS,
  VERA_NEAR_MISS,
  VERA_OPS_CHATTER,
  VERA_PARRY,
  VERA_SURPLUS_TIP,
  FENRIR_BOSS,
  FENRIR_COMEBACK,
  FENRIR_ENDLESS_TAUNT,
  FENRIR_GLOAT,
  FENRIR_GLOAT_ASCENDANT,
  FENRIR_HOARD_MOCK,
  FENRIR_NEAR_MISS,
  FENRIR_NEEDLE,
  FENRIR_PRESS_TAUNT,
  FENRIR_PURGED,
  FENRIR_RATTLED,
  FENRIR_SNARL,
  FENRIR_SPAWN,
  FENRIR_SHIELD_FIRST,
  VERA_SHIELD_FIRST,
} from "./commsContent.ts";

const DEFAULT_LOW_LIVES = 3;
const DEFAULT_COOLDOWN_MS = 9000;
const DEFAULT_MILESTONE_WAVES = 5;
const BARRAGE_WINDOW_MS = 4000;
const BARRAGE_COUNT = 3;
// A single core hit that costs more than one life reads as a heavy blow — the
// operator answers with a visibly sad/frustrated beat (PT-18).
const HEAVY_HIT_DAMAGE = 2;
const SWARM_THRESHOLD = 14;
const UPGRADE_TIP_WAVE = 3;
const SPAWN_BREACH_DEBOUNCE_MS = 1200;
const DOMINANCE_STREAK = 3;
// S193 (BL-272): run-phase awareness — milestone praise carries finale gravity
// from here on, and the first endless wave (Echo Mode) gets its own beat.
const FINALE_PRAISE_WAVE = 30;
const ENDLESS_ENTRY_WAVE = 36;
// S194 (BL-277 M1): a kill this close to the core (remaining waypoints) is a
// save at the door — worth a remark, but only if it FEELS rare (min spacing).
const NEAR_MISS_REMAINING = 3;
const NEAR_MISS_SPACING_WAVES = 3;
// S194 (BL-277 M2): the surplus teaching thresholds. The D-236 cap softens the
// wave bonus above 1,500 banked (floor ×0.3 at 3,500), Act II+ — VERA teaches
// once the bank is clearly in softening territory; FENRIR mocks the floor.
const SURPLUS_TIP_WAVE = 13;
const SURPLUS_TIP_GOLD = 2000;
const HOARD_MOCK_GOLD = 3500;
// S194 (BL-277 M3): the ambient system — fill genuine dead air only. Silent
// through the opening (W1-2) and the finale (W35, the story owns it).
const AMBIENT_QUIET_MS = 30000;
const AMBIENT_MIN_WAVE = 3;
const AMBIENT_MAX_WAVE = 34;
// S130: a hijack reads slower than a bark — extra hold beyond the length-based
// formula, and a small beat of dead air before VERA's scripted answer (W30).
const STORY_HIJACK_EXTRA_HOLD_MS = 1200;
const STORY_ANSWER_GAP_MS = 400;
// S193 (BL-269): the FIRST line of a call-and-response used to hold only the
// fixed 1.2-1.3 s phase delay — shorter than its own 34 ms/char type-on for
// most lines, so the full line was visible ~0.1-0.45 s. The first phase now
// always finishes typing and dwells ≥1.4 s while keeping the banter snappy.
function phaseHoldMs(line: string): number {
  return Math.max(2200, 34 * line.length + 1400);
}
// S193 (BL-269): ECHELON's dwell scales with her line — 106 ms/char covers the
// type-on (34) plus a slow combat-reading pace (~72 ms/char), so her longest
// offers are never cut before a reader finishes them.
function hijackHoldMs(baseHoldMs: number, line: string): number {
  return Math.max(baseHoldMs + STORY_HIJACK_EXTRA_HOLD_MS, line.length * 106);
}

export interface CommsDirectorDeps {
  /** The render surface the arbiter drives (BL-61: the commentary TV). Takes
   *  precedence over `el`. */
  surface?: MascotSurface;
  /** Legacy framed-portrait element — auto-wrapped in a DOM `MascotSurface`.
   *  Back-compat for any consumer (and the arbiter tests) that still pass a raw
   *  mascot element. Exactly one of `surface` / `el` must be provided. */
  el?: HTMLElement;
  getLives: () => number;
  lowLives?: number;
  cooldownMs?: number;
  milestoneWaves?: number;
  /** Task-013 (AC-17) — fires alongside `surface.popOutBeat` on the SAME three
   *  detected beats (boss spawn / new-signature debut / breach), so a caller
   *  that wants a side-effect beyond the TV's own CSS FX (native haptics) has
   *  one hook instead of re-deriving the beat detection. Optional; the arbiter
   *  itself stays platform-agnostic (no Capacitor import here). */
  onPopOutBeat?: ((beat: TvPopOutBeat) => void) | undefined;
}

export class CommsDirector {
  private readonly surface: MascotSurface;
  private readonly onPopOutBeat: ((beat: TvPopOutBeat) => void) | undefined;
  private readonly getLives: () => number;
  private readonly lowLives: number;
  private readonly milestoneWaves: number;
  private readonly scheduler: BeatScheduler;

  private currentWave = 1;
  private seenStealth = 0;
  private runStartDone = false;
  private hitGloatWave = -1;
  private barrageFiredWave = -1;
  private swarmFiredWave = -1;
  private aliveEnemies = 0;
  private hasPlacedTower = false;
  private hasUpgradedTower = false;
  private upgradeTipDone = false;
  private coreHitTimesMs: number[] = [];
  private spawnLockKey: string | null = null;
  private spawnLockUntil = 0;
  private firstBloodDone = false;
  private firstLeakDone = false;
  private seenEnemyTypes = new Set<string>();
  private seenShield = 0;
  // S193 (BL-272) — run-scoped memory: did this run ever brush the brink
  // (drives the hard-won victory callback), did the endless door already get
  // its voice, did the victory beat play, which acts already press-taunted.
  private brinkSeen = false;
  private endlessDone = false;
  private victoryDone = false;
  private pressTauntActs = new Set<number>();
  // S194 (BL-277) — run-scoped state: near-miss spacing, the once-per-run
  // economy teachings (consumed only when they actually play), the running
  // gold total for threshold-crossing detection, the ambient once-per-wave.
  private lastNearMissWave = -Infinity;
  private surplusTipDone = false;
  private hoardMockDone = false;
  private lastAmbientWave = -1;
  private readonly momentum: CommsMomentum;

  // S130 — the scripted story track (Story Bible §6.2): once-per-run beats at
  // fixed waves, through the SAME scheduler as the reactive barks. A blocked
  // beat is dropped, never queued into combat (GDD law).
  private storyPlayed = new Set<string>();
  private storyTimers: number[] = [];

  private unsubscribers: Array<() => void> = [];

  constructor(deps: CommsDirectorDeps) {
    const surface =
      deps.surface ?? (deps.el ? createDomMascotSurface(deps.el) : undefined);
    if (!surface) {
      throw new Error("CommsDirector requires a `surface` or an `el`");
    }
    this.surface = surface;
    this.onPopOutBeat = deps.onPopOutBeat;
    this.getLives = deps.getLives;
    this.lowLives = deps.lowLives ?? DEFAULT_LOW_LIVES;
    this.milestoneWaves = Math.max(
      1,
      deps.milestoneWaves ?? DEFAULT_MILESTONE_WAVES,
    );
    this.scheduler = new BeatScheduler(
      this.surface,
      deps.cooldownMs ?? DEFAULT_COOLDOWN_MS,
    );
    this.momentum = new CommsMomentum({
      lowLives: this.lowLives,
      dominanceStreak: DOMINANCE_STREAK,
    });
  }

  /**
   * Read-only window onto the rivalry tone (S126): the ECHELON corruption layer
   * harmonizes its intensity with FENRIR's momentum through this seam — one
   * threat, two channels. Pure delegation to the deterministic momentum model;
   * nothing here mutates state or emits events.
   */
  rivalryTone(livesLeft: number): RivalryTone {
    return this.momentum.getTone(livesLeft);
  }

  /**
   * Pick a bark and resolve it to the active locale (BL-89). The pick stays the
   * render-side RNG draw on the *key* pool (same order, sim-blind); `commsLine`
   * only turns the already-chosen key into display text — EN fallback so a raw
   * key never shows. Resolving here (not in the scheduler) keeps `holdMs` and
   * the dedup pool exactly as before.
   */
  private pickLine(pool: readonly string[]): string {
    return commsLine(this.scheduler.pick(pool));
  }

  /** Task-013 (AC-17) — notify the surface (+ any external hook, e.g. native
   *  haptics) that a pop-out beat landed. Fires independent of the comms
   *  cooldown/`canPlay` gate — the FX beat is about the GAME moment, not
   *  whether a bark happened to be free to play alongside it. */
  private firePopOut(beat: TvPopOutBeat): void {
    this.surface.popOutBeat?.(beat);
    this.onPopOutBeat?.(beat);
  }

  /**
   * Single-arbiter entry for the swap-screen commander reaction (BL-8). The
   * SkillOverview no longer picks its own quip — it asks the arbiter, which
   * also holds the portrait briefly so the same voice never doubles up when the
   * overlay closes.
   */
  narrateCardReaction(card: Card): string {
    const pool = COMMANDER_QUIPS[card.rarity] ?? COMMANDER_QUIPS.common;
    const line = this.pickLine(pool);
    this.scheduler.noteExternalSpeech(
      PRIORITY.MED,
      this.scheduler.holdMs(line),
    );
    return line;
  }

  /**
   * D-331 (grid re-route): BossArrival used to slam the boss's localized
   * name across the middle of the play grid. The owner's two-tier decision
   * moved that identity beat to the TV — this is its landing seam.
   * BossArrival still owns the real-boss guard + i18n name resolution (it
   * already has the enemies config table); it hands the resolved
   * `title`/`name` pair here rather than duplicating that lookup.
   *
   * HIGH priority (same tier as the existing `bossIncoming` FENRIR gloat this
   * plays alongside) so a boss's identity is never starved by a lower-tier
   * flavour line, but a genuine MAX beat (game-over) still wins.
   */
  announceBossArrival(title: string, name: string): void {
    const spec: BeatSpec = {
      id: "boss-arrival-name",
      priority: PRIORITY.HIGH,
      minor: false,
      speaker: "vera",
    };
    if (!this.scheduler.canPlay(spec)) return;
    const line = `${title}: ${name}`;
    this.scheduler.playVera(spec, "alert", line, this.scheduler.holdMs(line));
  }

  /**
   * D-331 (grid re-route): the generic landing seam for STORY/NARRATION/
   * PRAISE/STATUS text that used to be a centred `GameBanner` — "PROCESS
   * CLEAR", payload confirmations, the flawless-streak flourish, combo
   * milestones, breach narration, etc. Callers pick the priority tier from
   * the SAME `PRIORITY` ladder every other beat in this file uses (commsContent
   * `PRIORITY.AMBIENT..MAX`) rather than a parallel scheme, so a fresh boss/
   * breach beat is never stuck behind a queued flavour line, and a low-tier
   * status line never steals the slot from a higher one still holding it.
   */
  announceStatus(
    text: string,
    priority: BeatPriority = PRIORITY.LOW,
    pose: VeraPose = "idle",
  ): void {
    if (!text) return;
    const spec: BeatSpec = {
      id: `status-${text}`,
      priority,
      minor: priority <= PRIORITY.LOW,
      speaker: "vera",
    };
    if (!this.scheduler.canPlay(spec)) return;
    this.scheduler.playVera(spec, pose, text, this.scheduler.holdMs(text));
  }

  attach(bus: EventBus): void {
    this.detach();
    this.unsubscribers.push(
      bus.on("wave.started", ({ wave }) => {
        this.currentWave = wave;
        this.hitGloatWave = -1;
        this.barrageFiredWave = -1;
        this.swarmFiredWave = -1;
        this.aliveEnemies = 0;
        this.coreHitTimesMs = [];
        this.spawnLockKey = null;
        this.spawnLockUntil = 0;
        if (wave === 1) {
          this.momentum.reset();
          this.firstBloodDone = false;
          this.firstLeakDone = false;
          this.seenEnemyTypes.clear();
          this.seenShield = 0;
          this.brinkSeen = false;
          this.endlessDone = false;
          this.victoryDone = false;
          this.pressTauntActs.clear();
          this.lastNearMissWave = -Infinity;
          this.surplusTipDone = false;
          this.hoardMockDone = false;
          this.lastAmbientWave = -1;
          this.resetStoryTrack();
        }
        this.momentum.onWaveStarted();
        this.checkStoryBeats("wave.started", wave);
        // S193 (BL-272): the endless door — the first Echo wave gets a voice,
        // then FENRIR answers on the mocking `taunting` face (BL-274).
        if (wave === ENDLESS_ENTRY_WAVE && !this.endlessDone) {
          this.endlessDone = true;
          if (this.scheduler.canPlay(BEAT.endlessEntry)) {
            this.scheduler.clearTimers();
            const doorLine = this.pickLine(VERA_ENDLESS);
            const doorHold = phaseHoldMs(doorLine);
            this.scheduler.playVera(
              BEAT.endlessEntry,
              "alert",
              doorLine,
              doorHold,
            );
            this.scheduler.schedulePhase(doorHold, () => {
              const taunt = this.pickLine(FENRIR_ENDLESS_TAUNT);
              this.scheduler.playFenrir(
                "taunting",
                taunt,
                BEAT.endlessEntry.priority,
                this.scheduler.holdMs(taunt),
                false,
                true,
              );
            });
          }
        }
        if (!this.runStartDone && wave === 1) {
          this.runStartDone = true;
          this.scheduler.playVera(
            BEAT.runStart,
            "idle",
            this.pickLine(VERA_RUN_START),
          );
        }
        if (
          wave >= UPGRADE_TIP_WAVE &&
          this.hasPlacedTower &&
          !this.hasUpgradedTower &&
          !this.upgradeTipDone
        ) {
          this.upgradeTipDone = true;
          this.scheduler.playVera(
            BEAT.upgradeTip,
            "alert",
            this.pickLine(VERA_UPGRADE_TIP),
          );
        }
      }),
      bus.on("tower.placed", () => {
        this.hasPlacedTower = true;
      }),
      bus.on("tower.upgraded", () => {
        this.hasUpgradedTower = true;
      }),
      bus.on("wave.stealth.incoming", () => {
        // S193 (BL-272, the firstBlood pattern): the first-stealth TEACHING
        // beat only consumes its once-per-run slot when it actually plays —
        // otherwise the counter stayed advanced and the next telegraph lied
        // with "GHOSTS AGAIN." to a player who never heard call one.
        if (this.seenStealth === 0) {
          if (!this.scheduler.canPlay(BEAT.stealthFirst)) return;
          this.seenStealth = 1;
          this.scheduler.playVera(
            BEAT.stealthFirst,
            "alert",
            this.pickLine(VERA_STEALTH_FIRST),
          );
          return;
        }
        this.seenStealth += 1;
        this.scheduler.playVera(
          BEAT.stealthRepeat,
          "alert",
          this.pickLine(VERA_STEALTH_REPEAT),
        );
      }),
      bus.on("wave.shielded.incoming", () => {
        if (this.seenShield > 0) return;
        this.seenShield = 1;
        if (!this.scheduler.canPlay(BEAT.shieldFirst)) return;
        this.scheduler.clearTimers();
        const shieldLine = this.pickLine(FENRIR_SHIELD_FIRST);
        const shieldHold = phaseHoldMs(shieldLine);
        this.scheduler.playFenrir(
          "cheer",
          shieldLine,
          BEAT.shieldFirst.priority,
          shieldHold,
          false,
          true,
        );
        this.scheduler.schedulePhase(shieldHold, () => {
          this.scheduler.playVera(
            {
              id: "shield-first-response",
              priority: BEAT.shieldFirst.priority,
              minor: false,
              speaker: "vera",
            },
            "alert",
            this.pickLine(VERA_SHIELD_FIRST),
            undefined,
            true,
          );
        });
      }),
      bus.on("mainframe.hit", ({ damage, livesLeft }) => {
        const now = performance.now();
        this.coreHitTimesMs.push(now);
        this.pruneCoreHitTimes(now);
        this.momentum.onCoreHit(livesLeft);
        // S193 (BL-272): remember the brush with death for the victory callback.
        if (livesLeft <= this.lowLives) this.brinkSeen = true;

        if (livesLeft <= this.lowLives) {
          this.scheduler.playVera(
            BEAT.coreCritical,
            "alarm",
            this.pickLine(VERA_CRITICAL),
          );
          return;
        }

        // Heavy single blow (multi-life) — VERA reacts sad/frustrated (PT-18).
        // Routed through playVera/canPlay; ordinary 1-life leaks fall through to
        // the wary first-leak / gloat handling below.
        if (damage >= HEAVY_HIT_DAMAGE) {
          this.scheduler.playVera(
            BEAT.coreHeavyHit,
            "down",
            this.pickLine(VERA_HEAVY_HIT),
          );
          return;
        }

        // First leak of the run is VERA's wary "that was close" moment — the
        // villain's gloating only starts from the second hit onward.
        if (!this.firstLeakDone && this.scheduler.canPlay(BEAT.firstLeak)) {
          this.firstLeakDone = true;
          this.scheduler.playVera(
            BEAT.firstLeak,
            "alert",
            this.pickLine(VERA_FIRST_LEAK),
          );
          return;
        }

        if (
          this.coreHitTimesMs.length >= BARRAGE_COUNT &&
          this.barrageFiredWave !== this.currentWave
        ) {
          this.barrageFiredWave = this.currentWave;
          this.scheduler.playVera(
            BEAT.coreBarrage,
            "alarm",
            this.pickLine(VERA_BARRAGE),
          );
          return;
        }

        if (this.hitGloatWave === this.currentWave) return;
        if (!this.scheduler.canPlay(BEAT.coreHit)) return;
        this.hitGloatWave = this.currentWave;
        this.scheduler.clearTimers();
        const ascendant = this.momentum.getTone(livesLeft) === "ascendant";
        const gloatPool = ascendant ? FENRIR_GLOAT_ASCENDANT : FENRIR_GLOAT;
        const gloatLine = this.pickLine(gloatPool);
        const gloatHold = phaseHoldMs(gloatLine);
        this.scheduler.playFenrir(
          // S193 (BL-274): a pressing wolf toys with you — the mocking
          // `taunting` art carries the ascendant gloat (was generic cheer).
          ascendant ? "taunting" : "cheer",
          gloatLine,
          BEAT.coreHit.priority,
          gloatHold,
          BEAT.coreHit.minor,
        );
        this.scheduler.schedulePhase(gloatHold, () => {
          this.scheduler.playVera(
            {
              id: "core-hit-response",
              priority: BEAT.coreHit.priority,
              minor: false,
              speaker: "vera",
            },
            "down",
            this.pickLine(VERA_CORE_HIT),
            undefined,
            true,
          );
        });
      }),
      bus.on("breach.completed", () => {
        // Task-013 (AC-17) — the TV pop-out beat: a breach just happened.
        this.firePopOut("breach");
        this.tryCoreCritical();
      }),
      bus.on("breach.spawnActivating", ({ col, row }) => {
        const now = performance.now();
        const key = `${col},${row}`;
        if (this.spawnLockKey === key && now < this.spawnLockUntil) return;
        if (!this.scheduler.canPlay(BEAT.spawnBreach)) return;
        this.spawnLockKey = key;
        this.spawnLockUntil = now + SPAWN_BREACH_DEBOUNCE_MS;

        this.scheduler.clearTimers();
        const fenrirLine = this.pickLine(FENRIR_SPAWN);
        const spawnHold = phaseHoldMs(fenrirLine);
        this.scheduler.playFenrir(
          "cheer",
          fenrirLine,
          BEAT.spawnBreach.priority,
          spawnHold,
          false,
          true,
        );
        this.scheduler.schedulePhase(spawnHold, () => {
          this.scheduler.playVera(
            {
              id: "spawn-breach-response",
              priority: BEAT.spawnBreach.priority,
              minor: false,
              speaker: "vera",
            },
            "alarm",
            this.pickLine(VERA_SPAWN),
            undefined,
            true,
          );
        });
      }),
      bus.on("enemy.reachedEnd", () => this.tryCoreCritical()),
      // Enemy events are read-only signals used only for threshold counters.
      // Do not emit commentary per enemy spawn/kill; we fire once on threshold crossing.
      bus.on("enemy.spawned", ({ enemy }) => {
        const prev = this.aliveEnemies;
        this.aliveEnemies += 1;
        if (
          prev < SWARM_THRESHOLD &&
          this.aliveEnemies >= SWARM_THRESHOLD &&
          this.swarmFiredWave !== this.currentWave
        ) {
          this.swarmFiredWave = this.currentWave;
          this.scheduler.playVera(
            BEAT.swarmPressure,
            "worried", // S144: a swarm overwhelms — the line is bending
            this.pickLine(VERA_SWARM),
          );
          return;
        }
        // First sighting of a previously-unseen enemy type — wary curiosity.
        // The very first type of the run is recorded silently so the opening
        // line isn't cluttered; later new types earn a comment.
        const type = enemy?.type;
        if (!type) return;
        if (this.seenEnemyTypes.size === 0) {
          this.seenEnemyTypes.add(type);
          return;
        }
        if (!this.seenEnemyTypes.has(type)) {
          this.seenEnemyTypes.add(type);
          // Task-013 (AC-17) — the TV pop-out beat: this run's genuine first
          // sighting of the type (the SAME dedup the bark below uses — no
          // parallel "new signature" detection).
          this.firePopOut("newSignature");
          this.scheduler.playVera(
            BEAT.newSignature,
            "thinking", // S144: a new signature — she's reading/analyzing it
            this.pickLine(VERA_NEW_SIGNATURE),
          );
        }
      }),
      bus.on("tower.sold", ({ cause }) => {
        // Only a breach-forced loss is alert-worthy; manual sells are deliberate.
        if (cause !== "breach") return;
        this.scheduler.playVera(
          BEAT.nodeLost,
          "alert",
          this.pickLine(VERA_NODE_LOST),
        );
      }),
      bus.on("enemy.killed", ({ enemy }) => {
        this.aliveEnemies = Math.max(0, this.aliveEnemies - 1);
        // First kill of the run — a small encouraging nod. Gated on canPlay so
        // that if it collides with the opening line it simply retries on the
        // next kill instead of being silently consumed.
        if (!this.firstBloodDone && this.scheduler.canPlay(BEAT.firstBlood)) {
          this.firstBloodDone = true;
          this.scheduler.playVera(
            BEAT.firstBlood,
            "idle",
            this.pickLine(VERA_FIRST_BLOOD),
          );
          return;
        }
        this.tryNearMiss(enemy);
        this.maybeAmbient();
      }),
      bus.on("enemy.reachedEnd", () => {
        this.aliveEnemies = Math.max(0, this.aliveEnemies - 1);
      }),
      bus.on("failsafe.triggered", () => this.tryCoreCritical()),
      bus.on("boss.spawned", ({ wave }) => {
        // Task-013 (AC-17) — the TV pop-out beat: every boss arrival, not just
        // when a bark is free to play (the FX reads the game moment, not the
        // comms cooldown).
        this.firePopOut("boss");
        // The scripted track first: at the finale (W35) HER voice owns the
        // moment — the generic wolf line yields to the gate beat.
        this.checkStoryBeats("boss.spawned", wave ?? this.currentWave);
        if (!this.scheduler.canPlay(BEAT.bossIncoming)) return;
        this.scheduler.clearTimers();
        this.scheduler.playFenrir(
          "smug", // S144: the wolf savors the boss arrival — cocky, not neutral
          this.pickLine(FENRIR_BOSS),
          BEAT.bossIncoming.priority,
          4500,
          false,
          true,
        );
      }),
      bus.on("boss.killed", () => {
        if (!this.scheduler.canPlay(BEAT.bossDown)) return;
        this.scheduler.clearTimers();
        const bossDownLine = this.pickLine(VERA_BOSS_DOWN);
        const bossDownHold = phaseHoldMs(bossDownLine);
        this.scheduler.playVera(
          {
            id: "boss-down-win",
            priority: BEAT.bossDown.priority,
            minor: false,
            speaker: "vera",
          },
          "cheer",
          bossDownLine,
          bossDownHold,
        );
        this.scheduler.schedulePhase(bossDownHold, () => {
          // S193 (BL-272): a dominated wolf concedes with the grudging RATTLED
          // voice instead of the stock snarl — the gloat tone-routing pattern.
          const snarlPool =
            this.momentum.getTone(this.getLives()) === "rattled"
              ? FENRIR_RATTLED
              : FENRIR_SNARL;
          const snarl = this.pickLine(snarlPool);
          this.scheduler.playFenrir(
            "angry",
            snarl,
            BEAT.bossDown.priority,
            this.scheduler.holdMs(snarl),
            false,
            true,
          );
        });
      }),
      bus.on("wave.completed", ({ wave }) => {
        const outcome = this.momentum.onWaveCompleted();
        this.checkStoryBeats("wave.completed", wave ?? this.currentWave);

        // Clawed back from the brink — relief from VERA, frustration from FENRIR.
        if (
          outcome.stabilizedFromBrink &&
          this.scheduler.canPlay(BEAT.comeback)
        ) {
          this.scheduler.clearTimers();
          const comebackLine = this.pickLine(VERA_COMEBACK);
          const comebackHold = phaseHoldMs(comebackLine);
          this.scheduler.playVera(
            BEAT.comeback,
            "cheer",
            comebackLine,
            comebackHold,
          );
          this.scheduler.schedulePhase(comebackHold, () => {
            const snarl = this.pickLine(FENRIR_COMEBACK);
            this.scheduler.playFenrir(
              "angry",
              snarl,
              BEAT.comeback.priority,
              this.scheduler.holdMs(snarl),
              false,
              true,
            );
          });
          return;
        }

        // Sustained flawless streak — confidence from VERA, FENRIR off-balance.
        if (
          outcome.flawlessMilestone &&
          this.scheduler.canPlay(BEAT.flawlessStreak)
        ) {
          this.scheduler.clearTimers();
          const flawlessLine = this.pickLine(VERA_FLAWLESS);
          const flawlessHold = phaseHoldMs(flawlessLine);
          this.scheduler.playVera(
            BEAT.flawlessStreak,
            "cheer",
            flawlessLine,
            flawlessHold,
          );
          this.scheduler.schedulePhase(flawlessHold, () => {
            const taunt = this.pickLine(FENRIR_RATTLED);
            this.scheduler.playFenrir(
              "angry",
              taunt,
              BEAT.flawlessStreak.priority,
              this.scheduler.holdMs(taunt),
              false,
              true,
            );
          });
          return;
        }

        const tone = this.momentum.getTone(this.getLives());

        if (!wave || wave % this.milestoneWaves !== 0) {
          // S193 (BL-272): the once-per-act press-taunt — between bosses, when
          // the wolf smells blood (ascendant), he fills the quiet on the
          // mocking `taunting` face. LOW + minor: it never talks over anything.
          if (wave && tone === "ascendant") {
            const act = wave <= 12 ? 1 : wave <= 24 ? 2 : 3;
            if (
              !this.pressTauntActs.has(act) &&
              this.scheduler.canPlay(BEAT.pressTaunt)
            ) {
              this.pressTauntActs.add(act);
              const taunt = this.pickLine(FENRIR_PRESS_TAUNT);
              this.scheduler.playFenrir(
                "taunting",
                taunt,
                BEAT.pressTaunt.priority,
                this.scheduler.holdMs(taunt),
                true,
                true,
              );
            }
          }
          return;
        }

        // S193 (BL-272): milestone praise is tone- and phase-routed — the
        // FENRIR-gloat pattern generalized. The endgame outranks the tone.
        const praisePool =
          wave >= FINALE_PRAISE_WAVE
            ? VERA_WAVE_PRAISE_FINALE
            : tone === "rattled"
              ? VERA_WAVE_PRAISE_DOMINANT
              : tone === "ascendant"
                ? VERA_WAVE_PRAISE_RELIEF
                : VERA_WAVE_PRAISE;
        this.scheduler.playVera(
          BEAT.wavePraise,
          // Quiet approval; the relief variant reads calm-after-the-storm.
          tone === "ascendant" && wave < FINALE_PRAISE_WAVE ? "idle" : "proud",
          this.pickLine(praisePool),
        );
      }),
      bus.on("payload.detonated", ({ type }) => {
        // GOLD kernel_panic earns the exclusive WOW flourish: glasses fly off,
        // frame surges to gold, glitter burst.
        if (type === "kernelPanic") {
          if (!this.scheduler.canPlay(BEAT.kernelPanic)) return;
          this.scheduler.clearTimers();
          this.scheduler.playVera(
            BEAT.kernelPanic,
            "wow",
            this.pickLine(VERA_KERNEL_PANIC),
          );
          this.surface.burstGlitter();
          return;
        }
        // Any other payload (e.g. the violet Memory Leak / reduceMaxHp) gets a
        // modest happy bark — cheer pose, frame STAYS GREEN, no glitter.
        if (!this.scheduler.canPlay(BEAT.payloadLaunch)) return;
        this.scheduler.playVera(
          BEAT.payloadLaunch,
          "cheer",
          this.pickLine(VERA_PAYLOAD_LAUNCH),
        );
      }),
      bus.on("game.over", () => {
        this.scheduler.playGameOver(this.pickLine(VERA_GAME_OVER));
      }),
      // S194 (BL-277 M2): economy intelligence rides the EXISTING gold-total
      // events — threshold-crossing detection is pure render-side bookkeeping.
      bus.on("economy.earned", ({ total }) => this.onGoldTotal(total)),
      bus.on("economy.bonus", ({ total }) => this.onGoldTotal(total)),
      // S193 (BL-272/BL-274): the run is WON — the rivalry finally resolves on
      // the broadcast itself. VERA signs off (the hard-won callback remembers a
      // brink run), then FENRIR concedes on the `defeated` face — art that
      // never aired in live comms before.
      bus.on("game.won", () => {
        if (this.victoryDone) return;
        this.victoryDone = true;
        this.scheduler.clearTimers();
        const pool = this.brinkSeen ? VERA_VICTORY_HARDWON : VERA_VICTORY;
        const signOff = this.pickLine(pool);
        const signOffHold = phaseHoldMs(signOff);
        this.scheduler.playVera(
          BEAT.victory,
          this.brinkSeen ? "cheer" : "proud",
          signOff,
          signOffHold,
        );
        this.scheduler.schedulePhase(signOffHold, () => {
          const concession = this.pickLine(FENRIR_PURGED);
          this.scheduler.playFenrir(
            "defeated",
            concession,
            BEAT.victory.priority,
            this.scheduler.holdMs(concession),
            false,
            true,
          );
        });
      }),
    );
  }

  // ── S130: the scripted story track (Story Bible §6.2) ──────────────────────

  /** Fire any scripted beat registered for this trigger+wave, once per run. A
   *  beat with `delayMs` waits (the act card owns the act door first) and is
   *  re-gated through canPlay at fire time — blocked beats drop, never queue. */
  private checkStoryBeats(trigger: StoryTrigger, wave: number): void {
    for (const beat of STORY_BEATS) {
      if (beat.trigger !== trigger || beat.wave !== wave) continue;
      if (this.storyPlayed.has(beat.id)) continue;
      this.storyPlayed.add(beat.id);
      if (beat.delayMs && beat.delayMs > 0) {
        this.storyTimers.push(
          window.setTimeout(() => this.playStoryBeat(beat), beat.delayMs),
        );
      } else {
        this.playStoryBeat(beat);
      }
    }
  }

  private playStoryBeat(beat: StoryBeat): void {
    const spec = {
      id: `story-${beat.id}`,
      priority: PRIORITY.HIGH,
      minor: false,
      speaker: beat.speaker,
    };
    if (beat.speaker === "echelon") {
      if (!this.scheduler.canPlay(spec)) return;
      const lineText = commsLine(beat.key);
      const holdMs = hijackHoldMs(this.scheduler.holdMs(lineText), lineText);
      this.scheduler.playEchelon(lineText, spec.priority, holdMs);
      if (beat.followUp) {
        const fu = beat.followUp;
        this.storyTimers.push(
          window.setTimeout(() => {
            this.scheduler.playVera(
              {
                id: `story-${beat.id}-answer`,
                priority: PRIORITY.HIGH,
                minor: false,
                speaker: "vera",
              },
              fu.pose,
              commsLine(fu.key),
            );
          }, holdMs + STORY_ANSWER_GAP_MS),
        );
      }
      return;
    }
    this.scheduler.playVera(spec, beat.pose ?? "idle", commsLine(beat.key));
  }

  private resetStoryTrack(): void {
    this.storyPlayed.clear();
    for (const timer of this.storyTimers) clearTimeout(timer);
    this.storyTimers = [];
  }

  /** S64-F06: Reset all run-scoped counters so 2nd-run barks fire again. */
  resetRunState(): void {
    this.currentWave = 1;
    this.seenStealth = 0;
    this.runStartDone = false;
    this.hitGloatWave = -1;
    this.barrageFiredWave = -1;
    this.swarmFiredWave = -1;
    this.aliveEnemies = 0;
    this.hasPlacedTower = false;
    this.hasUpgradedTower = false;
    this.upgradeTipDone = false;
    this.coreHitTimesMs = [];
    this.spawnLockKey = null;
    this.spawnLockUntil = 0;
    this.firstBloodDone = false;
    this.firstLeakDone = false;
    this.seenEnemyTypes = new Set<string>();
    this.seenShield = 0;
    this.brinkSeen = false;
    this.endlessDone = false;
    this.victoryDone = false;
    this.pressTauntActs.clear();
    this.lastNearMissWave = -Infinity;
    this.surplusTipDone = false;
    this.hoardMockDone = false;
    this.lastAmbientWave = -1;
    this.resetStoryTrack();
  }

  detach(): void {
    for (const off of this.unsubscribers) off();
    this.unsubscribers = [];
    this.scheduler.clearTimers();
    this.resetStoryTrack();
  }

  // ── S194 (BL-277 M1): the near-miss save ────────────────────────────────────
  // The enemy.killed payload already carries the walk state (path/pathIndex →
  // remaining waypoints to the core) — no sim event needed. Tone-routed: VERA's
  // relief normally, FENRIR's mock (taunting) when he is pressing. Min 3-wave
  // spacing so a door-side kill zone doesn't turn the save into wallpaper.
  private tryNearMiss(enemy: { path?: { x: number; y: number }[] | null; pathIndex?: number } | undefined): void {
    const path = enemy?.path;
    const idx = enemy?.pathIndex;
    if (!path || typeof idx !== "number" || idx <= 0) return;
    if (path.length - idx > NEAR_MISS_REMAINING) return;
    if (this.currentWave - this.lastNearMissWave < NEAR_MISS_SPACING_WAVES) {
      return;
    }
    if (this.momentum.getTone(this.getLives()) === "ascendant") {
      if (!this.scheduler.canPlay(BEAT.nearMissMock)) return;
      this.lastNearMissWave = this.currentWave;
      const mock = this.pickLine(FENRIR_NEAR_MISS);
      this.scheduler.playFenrir(
        "taunting",
        mock,
        BEAT.nearMissMock.priority,
        this.scheduler.holdMs(mock),
        true,
      );
      return;
    }
    if (!this.scheduler.canPlay(BEAT.nearMiss)) return;
    this.lastNearMissWave = this.currentWave;
    this.scheduler.playVera(
      BEAT.nearMiss,
      "alert",
      this.pickLine(VERA_NEAR_MISS),
    );
  }

  // ── S194 (BL-277 M2): economy intelligence ──────────────────────────────────
  // Teaches the D-236 surplus cap the moment it is costing the player at a
  // wave where the cap is armed (level checks, not edge detection — a player
  // already rich BEFORE Act II still gets taught). Once per run each, consumed
  // only when the line actually plays (the stealth-first law); a blocked try
  // simply retries on the next gold event. FENRIR mocks the bonus-floor war
  // chest — and only ever after VERA taught first (mock without the lesson
  // would be unfair).
  private onGoldTotal(total: number): void {
    if (this.currentWave < SURPLUS_TIP_WAVE) return;
    if (!this.surplusTipDone) {
      if (total < SURPLUS_TIP_GOLD) return;
      if (!this.scheduler.canPlay(BEAT.surplusTip)) return;
      this.surplusTipDone = true;
      this.scheduler.playVera(
        BEAT.surplusTip,
        "thinking",
        this.pickLine(VERA_SURPLUS_TIP),
      );
      return;
    }
    if (this.hoardMockDone || total < HOARD_MOCK_GOLD) return;
    if (!this.scheduler.canPlay(BEAT.hoardMock)) return;
    this.hoardMockDone = true;
    const mock = this.pickLine(FENRIR_HOARD_MOCK);
    this.scheduler.playFenrir(
      "taunting",
      mock,
      BEAT.hoardMock.priority,
      this.scheduler.holdMs(mock),
      true,
    );
  }

  // ── S194 (BL-277 M3): the dead-air killer ───────────────────────────────────
  // Event-driven (rides enemy.killed — no new timers): when the feed has been
  // quiet ≥30 s mid-wave, one tone-routed ambient beat per wave. LOW + minor by
  // law — it fills silence, it never competes.
  private maybeAmbient(): void {
    const w = this.currentWave;
    if (w < AMBIENT_MIN_WAVE || w > AMBIENT_MAX_WAVE) return;
    if (this.lastAmbientWave === w) return;
    if (this.scheduler.quietMs() < AMBIENT_QUIET_MS) return;
    const tone = this.momentum.getTone(this.getLives());
    if (tone === "rattled") {
      // The wolf probes to save face; VERA parries cool — the rivalry
      // crackles precisely when the player is dominating.
      if (!this.scheduler.canPlay(BEAT.ambientNeedle)) return;
      this.lastAmbientWave = w;
      this.scheduler.clearTimers();
      const needle = this.pickLine(FENRIR_NEEDLE);
      const needleHold = phaseHoldMs(needle);
      this.scheduler.playFenrir(
        "smug",
        needle,
        BEAT.ambientNeedle.priority,
        needleHold,
        true,
      );
      this.scheduler.schedulePhase(needleHold, () => {
        this.scheduler.playVera(
          {
            id: "ambient-parry",
            priority: BEAT.ambientNeedle.priority,
            minor: false,
            speaker: "vera",
          },
          "idle",
          this.pickLine(VERA_PARRY),
          undefined,
          true,
        );
      });
      return;
    }
    if (tone === "ascendant") {
      // Quiet while the player bleeds — the press pool, quiet-gated (the
      // once-per-act wave.completed beat keeps its own guard).
      if (!this.scheduler.canPlay(BEAT.ambientNeedle)) return;
      this.lastAmbientWave = w;
      const taunt = this.pickLine(FENRIR_PRESS_TAUNT);
      this.scheduler.playFenrir(
        "taunting",
        taunt,
        BEAT.ambientNeedle.priority,
        this.scheduler.holdMs(taunt),
        true,
      );
      return;
    }
    if (!this.scheduler.canPlay(BEAT.ambientOps)) return;
    this.lastAmbientWave = w;
    this.scheduler.playVera(
      BEAT.ambientOps,
      "idle",
      this.pickLine(VERA_OPS_CHATTER),
    );
  }

  private tryCoreCritical(): void {
    if (this.getLives() > this.lowLives) return;
    this.scheduler.playVera(
      BEAT.coreCritical,
      "alarm",
      this.pickLine(VERA_CRITICAL),
    );
  }

  private pruneCoreHitTimes(nowMs: number): void {
    const cutoff = nowMs - BARRAGE_WINDOW_MS;
    this.coreHitTimesMs = this.coreHitTimesMs.filter((t) => t >= cutoff);
  }
}
